import { useConversationParticipants } from "@app/hooks/conversations";
import { useAppRouter } from "@app/lib/platform";
import { usePeople, usePeopleActions } from "@app/lib/swr/people";
import { getConversationRoute } from "@app/lib/utils/router";
import type { LightWorkspaceType } from "@app/types/user";
import {
  Avatar,
  ContentMessage,
  Dialog,
  DialogContainer,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  SearchInput,
} from "@dust-tt/sparkle";
import { useState } from "react";

interface InviteToConversationDialogProps {
  owner: LightWorkspaceType;
  conversationId: string;
  isOpen: boolean;
  onClose: () => void;
}

/**
 * P10: inviting a human to a thread they were not in exposes everything already
 * said in it, including the private half with a personal assistant. The warning
 * is stated before the invite lands, and the way out — a new shared
 * conversation that starts empty — is offered next to it.
 */
export function InviteToConversationDialog({
  owner,
  conversationId,
  isOpen,
  onClose,
}: InviteToConversationDialogProps) {
  const router = useAppRouter();
  const { humans, isPeopleLoading } = usePeople({ owner, disabled: !isOpen });
  const { startConversation, inviteToConversation } = usePeopleActions({
    owner,
  });

  const [searchTerm, setSearchTerm] = useState("");
  const [selected, setSelected] = useState<string | null>(null);
  const [isBusy, setIsBusy] = useState(false);

  const { conversationParticipants } = useConversationParticipants({
    conversationId,
    workspaceId: owner.sId,
    options: { disabled: !isOpen },
  });
  const alreadyIn = new Set(
    conversationParticipants?.users.map((user) => user.sId) ?? []
  );

  const needle = searchTerm.trim().toLowerCase();
  const candidates = humans.filter(
    (human) =>
      !human.isSelf &&
      !alreadyIn.has(human.sId) &&
      (needle === "" ||
        human.fullName.toLowerCase().includes(needle) ||
        human.email.toLowerCase().includes(needle))
  );
  const selectedHuman = humans.find((human) => human.sId === selected) ?? null;

  const close = () => {
    setSelected(null);
    setSearchTerm("");
    onClose();
  };

  const onInvite = async () => {
    if (!selected) {
      return;
    }

    setIsBusy(true);
    const result = await inviteToConversation(conversationId, selected);
    setIsBusy(false);
    if (result.isOk()) {
      close();
    }
  };

  const onStartFresh = async () => {
    if (!selected) {
      return;
    }

    setIsBusy(true);
    const result = await startConversation({ kind: "human", sId: selected });
    setIsBusy(false);
    if (result.isOk()) {
      close();
      void router.push(
        getConversationRoute(owner.sId, result.value.conversationId)
      );
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && close()}>
      <DialogContent size="md">
        <DialogHeader>
          <DialogTitle>Invite someone to this conversation</DialogTitle>
        </DialogHeader>
        <DialogContainer>
          <div className="flex flex-col gap-4">
            <ContentMessage
              title="They will see the whole conversation"
              variant="warning"
            >
              {selectedHuman
                ? `${selectedHuman.fullName} will be able to read this conversation from its first message, including everything you and your agents already said in it.`
                : "Whoever you invite will be able to read this conversation from its first message, including everything you and your agents already said in it."}{" "}
              To keep this history private, start a new shared conversation
              instead — it begins empty, with an agent in it.
            </ContentMessage>

            <SearchInput
              placeholder="Search members"
              value={searchTerm}
              name="invite-search"
              onChange={setSearchTerm}
            />

            {isPeopleLoading && (
              <p className="copy-sm text-muted-foreground">Loading members…</p>
            )}

            <div className="flex max-h-64 flex-col gap-1 overflow-y-auto">
              {candidates.map((human) => (
                <button
                  key={human.sId}
                  type="button"
                  onClick={() => setSelected(human.sId)}
                  className={`flex items-center gap-2 rounded-xl border p-2 text-left ${
                    selected === human.sId
                      ? "border-highlight-400 bg-muted-background"
                      : "border-transparent hover:bg-muted-background"
                  }`}
                >
                  <Avatar
                    size="xs"
                    visual={human.image}
                    name={human.fullName}
                    isRounded
                  />
                  <span className="copy-sm min-w-0 truncate text-foreground">
                    {human.fullName}
                  </span>
                  <span className="copy-xs min-w-0 truncate text-muted-foreground">
                    {human.email}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </DialogContainer>
        <DialogFooter
          leftButtonProps={{
            label: "Start a new shared conversation",
            variant: "outline",
            disabled: !selected || isBusy,
            onClick: () => void onStartFresh(),
          }}
          rightButtonProps={{
            label: "Invite to this conversation",
            variant: "warning",
            disabled: !selected || isBusy,
            onClick: () => void onInvite(),
          }}
        />
      </DialogContent>
    </Dialog>
  );
}
